import { Box, SpaceBetween, Spinner } from '@cloudscape-design/components';
import {
  PartTypes,
} from '@parallel-fluidics/shared/constants';
import { decodePPartId, getPPartLabel } from '@parallel-fluidics/shared/models/projects/parts';
import useGetProjectPartByDocId from 'features/pPart/hooks/useGetProjectPartByDocId';
import {
  useGetProjectByDocId,
  useGetProjectById,
  useGetProjectParts,
} from 'hooks/use-basicQueries';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';

import PPartRefBadge from './pPartRefBadge';

export default function PPartDisplay({
  fieldName,
  projectId = null,
  projectDocId = null,
  pPartId = null,
  pPartDocId = null,
  fallbackMessage = '',
  emptyMessage = null,
  showEmptyText = false,
  CustomWrapper = null,
  loading = false,
}) {
  // if projectDocId is exists, we disable the query to get project
  const { data: project, isLoading: projectLoading } = useGetProjectById(projectId, !!projectDocId);
  // if pPartDocId is exists, we disable the query to get product parts
  const { data: productParts, isLoading: productPartsLoading } = useGetProjectParts({
    projectDocId: projectDocId || project?.id,
    type: PartTypes.PRODUCT,
    disabled: !pPartId || !!pPartDocId,
  });
  // if pPartDocId is provided, we skip the productParts query and use it directly
  const {
    data: pPartFromDoc, isLoading: pPartFromDocLoading,
  } = useGetProjectPartByDocId({ projectDocId, pPartDocId });
  // fullId prefixes the project's name — the doc fetch only exists to
  // resolve it, so skip it when the caller already passed projectId
  const { data: projectFromDoc } = useGetProjectByDocId(
    fieldName === 'fullId' ? projectDocId : null,
    Boolean(projectId),
  );

  const findPart = useMemo(() => {
    if (!productParts?.length && !pPartFromDoc) return null;
    if (pPartFromDoc) return pPartFromDoc;
    if (!pPartId) return null;
    const { partId, revision, material } = decodePPartId(pPartId);
    return productParts?.find((part) => part.partId === partId
          && part.revision === revision
          && part.material === material);
  }, [productParts, pPartId, pPartFromDoc]);

  const Wrapper = CustomWrapper || React.Fragment;
  // The notes variant is the "part step info" surface: it renders the notes
  // text plus the part's BOM and linked-tool badges everywhere it's used.
  const showComponents = fieldName === 'notes' && findPart?.components?.length > 0;
  const showLinkedParts = fieldName === 'notes' && findPart?.linkedParts?.length > 0;
  const projectName = projectId || (project || projectFromDoc)?.name;

  const content = useMemo(() => {
    // "P068-0068-A-COC (Pipette interface - Product)" — the standard label
    if (fieldName === 'fullId' && findPart) {
      return getPPartLabel(findPart, projectName);
    }
    if (findPart?.[fieldName]) return findPart[fieldName];
    if (fallbackMessage) return fallbackMessage;
    if (showEmptyText && emptyMessage) {
      return <Box color="text-status-inactive" variant="i">{emptyMessage}</Box>;
    }
    return null;
  }, [emptyMessage, fallbackMessage, fieldName, findPart, projectName, showEmptyText]);

  // Nothing until the part resolves — otherwise the fallback / empty
  // message flashes before the real notes replace it. `loading` is the
  // caller's own pending lookups (the traveler page's project query, which
  // the IDs here depend on); a missing ID alone can't be read as loading,
  // since a failed lookup would then spin forever instead of falling back.
  const resolving = loading || projectLoading || productPartsLoading || pPartFromDocLoading;
  if (!findPart && resolving) {
    // The notes variant holds its slot while loading; the inline variants
    // (name, fullId, ...) just stay blank.
    if (fieldName !== 'notes' || !CustomWrapper) return null;
    return (
      <Wrapper>
        <Spinner size="normal" />
      </Wrapper>
    );
  }
  if (!content && !showLinkedParts && !showComponents) return null;

  return (
    <Wrapper>
      <span style={{ display: 'inline-flex', gap: 4, flexDirection: 'column' }}>
        {content && (
          <span style={{ whiteSpace: 'pre-line' }}>
            {content}
          </span>
        )}
        {showComponents && (
          <SpaceBetween size="xxs" direction="horizontal">
            {findPart.components.map((component) => (
              <PPartRefBadge
                key={`${component.projectDocId}/${component.pPartDocId}`}
                projectDocId={component.projectDocId}
                pPartDocId={component.pPartDocId}
                quantity={component.quantity}
              />
            ))}
          </SpaceBetween>
        )}
        {showLinkedParts && (
          <SpaceBetween size="xxs" direction="horizontal">
            {findPart.linkedParts.map((row) => (
              <PPartRefBadge
                key={`${row.projectDocId}/${row.pPartDocId}`}
                projectDocId={row.projectDocId}
                pPartDocId={row.pPartDocId}
              />
            ))}
          </SpaceBetween>
        )}
      </span>
    </Wrapper>
  );
}

PPartDisplay.propTypes = {
  fieldName: PropTypes.oneOf(['notes', 'name', 'stepType', 'fullId']).isRequired,
  projectId: PropTypes.string,
  pPartId: PropTypes.string,
  projectDocId: PropTypes.string,
  pPartDocId: PropTypes.string,
  fallbackMessage: PropTypes.string,
  CustomWrapper: PropTypes.elementType,
  emptyMessage: PropTypes.node,
  showEmptyText: PropTypes.bool,
  /** The caller is still resolving the IDs it passes — hold the fallback. */
  loading: PropTypes.bool,
};
